import { api, fetchServer } from "@/features/utils/fetch-server";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

async function postLogin(
  publicId: string,
  path: "challenge" | "verify" | "call",
  body: unknown,
) {
  const response = await fetchServer(
    `/api/sessions/v1/public/${publicId}/login/${path}`,
    {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err?.error || "Request failed");
  }

  return response.json();
}

// Step 1: send a one-time code to the given phone number.
export function usePublicLoginChallenge(publicId: string) {
  return useMutation({
    mutationFn: (phone: string) => postLogin(publicId, "challenge", { phone }),
  });
}

// Step 2: verify the code. On success the backend re-binds the session to the
// verified customer, so refetch both the customer and the session.
export function usePublicLoginVerify(publicId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      phone,
      code,
      resume,
    }: {
      phone: string;
      code: string;
      resume?: boolean;
    }) => postLogin(publicId, "verify", { phone, code, resume }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["public-customer", publicId],
      });
      queryClient.invalidateQueries({
        queryKey: ["public-session", publicId],
      });
    },
  });
}

// The other way out: rather than wait for the SMS, be called on that number and carry
// on with the agent on the phone. Placing the call signs nothing in; the visitor saying
// "oui, c'est moi" to the agent, shortly after, does.
export function usePublicPhoneHandoff(publicId: string) {
  return useMutation({
    mutationFn: (phone: string): Promise<{ status: "ringing" }> =>
      postLogin(publicId, "call", { phone }),
  });
}

export type PhoneHandoffStatus =
  | "none"
  | "calling"
  | "verified"
  | "failed"
  | "ended";

// What the "appel en cours" screen polls while the call is on. A sign-in lands by
// socket as well; the poll is what notices a call that ended without one.
export function usePhoneHandoffStatus(publicId: string, enabled: boolean) {
  return useQuery({
    queryKey: ["public-phone-handoff", publicId],
    queryFn: (): Promise<{ status: PhoneHandoffStatus }> =>
      api.get(`/api/sessions/v1/public/${publicId}/login/call`, {
        credentials: "include",
      }),
    enabled,
    refetchInterval: enabled ? 2500 : false,
    gcTime: 0,
  });
}
